import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, BookOpen, CheckCircle, Edit, Lock, Plus,
  Save, Shield, Trash2, Users, X, CreditCard, Percent,
} from "lucide-react";
import { toast } from "sonner";
import { AuthContext } from "../App";
import DateInput from "../components/ui/DateInput";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const USOS = [
  { value: "venta_contado",       label: "Venta contado",          tipo: "cobrar" },
  { value: "venta_credito",       label: "Venta crédito",          tipo: "cobrar" },
  { value: "compra_contado",      label: "Compra contado",         tipo: "pagar"  },
  { value: "compra_credito",      label: "Compra crédito",         tipo: "pagar"  },
  { value: "saldo_favor_cliente",   label: "Saldo a favor clientes",   tipo: "cobrar", soloInfo: true, desc: "Créditos acumulados por notas de crédito de clientes. Se aplican al cobrar." },
  { value: "saldo_favor_proveedor", label: "Saldo a favor proveedores", tipo: "pagar",  soloInfo: true, desc: "Créditos o saldos a favor del proveedor. Se aplican al pagar compras." },
  { value: "prestamo_empleados",    label: "Préstamos a empleados",    tipo: "cobrar", soloInfo: true, desc: "Préstamos otorgados a funcionarios. Se cobran por cuotas o desde sueldo." },
  { value: "prestamo_proveedores",  label: "Préstamos a proveedores",  tipo: "cobrar", soloInfo: true, desc: "Préstamos otorgados a proveedores. Se controlan desde el módulo financiero." },
  { value: "prestamo_bancarios",    label: "Préstamos bancarios",      tipo: "pagar",  soloInfo: true, desc: "Préstamos recibidos de entidades bancarias. Se pagan por cuotas." },
  { value: "anticipo_proveedores",  label: "Anticipos a proveedores",  tipo: "cobrar", soloInfo: true, desc: "Anticipos entregados a proveedores. Quedan como saldo a cobrar/aplicar contra compras." },
];

const emptyForm = (logo) => ({
  logo_tipo: logo || "arandujar",
  nombre: "",
  tipo: "cobrar",
  uso: "venta_contado",
  dias_vencimiento: 0,
  fecha_vencimiento_fija: "",
  predeterminada: false,
  activa: true,
  notas: "",
  es_cuotas: false,
  max_cuotas: 6,
  tasa_interes: 0,
});

export default function PlanCuentasPage() {
  const { token, activeEmpresaPropia, hasPermission, user: currentUser } = useContext(AuthContext);
  const logo = activeEmpresaPropia?.slug || "arandujar";

  const [cuentas, setCuentas]         = useState([]);
  const [usuarios, setUsuarios]       = useState([]);
  const [loading, setLoading]         = useState(false);
  const [showForm, setShowForm]       = useState(false);
  const [editing, setEditing]         = useState(null);
  const [form, setForm]               = useState(emptyForm(logo));
  const [accesoModal, setAccesoModal] = useState(null);
  const [accesoIds, setAccesoIds]     = useState([]);
  const [savingAcceso, setSavingAcceso] = useState(false);
  // Cuotas acceso modal
  const [cuotasModal, setCuotasModal]   = useState(null);
  const [cuotasConfig, setCuotasConfig] = useState([]); // [{user_id, max_cuotas}]
  const [savingCuotas, setSavingCuotas] = useState(false);

  const canCreate          = hasPermission?.("plan_cuentas.crear");
  const canEdit            = hasPermission?.("plan_cuentas.editar");
  const canDelete          = hasPermission?.("plan_cuentas.eliminar");
  const canAsignarAcceso   = hasPermission?.("plan_cuentas.asignar_cuentas")
    || ["admin", "super_admin", "gerente"].includes(currentUser?.role);
  const canGestionarCuotas = hasPermission?.("plan_cuentas.gestionar_cuotas")
    || ["admin", "super_admin", "gerente"].includes(currentUser?.role);

  // ── Fetch ─────────────────────────────────────────────────────────────────────

  const fetchCuentas = async () => {
    setLoading(true);
    try {
      const q   = logo ? `?logo_tipo=${logo}` : "";
      const res = await fetch(`${API}/admin/plan-cuentas${q}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("No se pudo cargar el plan de cuentas");
      setCuentas(await res.json());
    } catch (e) {
      toast.error(e.message || "Error al cargar plan de cuentas");
    } finally {
      setLoading(false);
    }
  };

  const fetchUsuarios = async () => {
    try {
      const res = await fetch(`${API}/admin/usuarios`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const all = await res.json();
        setUsuarios(
          all.filter(u =>
            u.role === "usuario" &&
            (u.logos_asignados || []).map(String).includes(String(activeEmpresaPropia?.id || ""))
          )
        );
      }
    } catch {}
  };

  useEffect(() => {
    fetchCuentas();
    if (canAsignarAcceso) fetchUsuarios();
  }, [logo]); // eslint-disable-line

  const grouped = useMemo(
    () => USOS.map(u => ({ ...u, cuentas: cuentas.filter(c => c.uso === u.value) })),
    [cuentas]
  );

  // ── CRUD ──────────────────────────────────────────────────────────────────────

  const openNew = () => {
    setEditing(null);
    setForm(emptyForm(logo));
    setShowForm(true);
  };

  const openEdit = (cuenta) => {
    setEditing(cuenta);
    setForm({
      logo_tipo:        cuenta.logo_tipo || logo,
      nombre:           cuenta.nombre || "",
      tipo:             cuenta.tipo || "cobrar",
      uso:              cuenta.uso || "venta_contado",
      dias_vencimiento: cuenta.dias_vencimiento ?? 0,
      fecha_vencimiento_fija: cuenta.fecha_vencimiento_fija || "",
      predeterminada:   !!cuenta.predeterminada,
      activa:           cuenta.activa !== false,
      notas:            cuenta.notas || "",
      es_cuotas:        !!cuenta.es_cuotas,
      max_cuotas:       cuenta.max_cuotas ?? 6,
      tasa_interes:     cuenta.tasa_interes ?? 0,
    });
    setShowForm(true);
  };

  // ── Cuotas acceso ─────────────────────────────────────────────────────────────

  const openCuotasModal = (cuenta) => {
    setCuotasModal(cuenta);
    setCuotasConfig((cuenta.usuarios_cuotas || []).map(c => ({
      user_id: String(c.user_id),
      max_cuotas: c.max_cuotas ?? cuenta.max_cuotas ?? 6,
    })));
  };

  const toggleCuotasUser = (uid) => {
    const id = String(uid);
    setCuotasConfig(prev => {
      const exists = prev.find(c => c.user_id === id);
      if (exists) return prev.filter(c => c.user_id !== id);
      return [...prev, { user_id: id, max_cuotas: cuotasModal?.max_cuotas ?? 6 }];
    });
  };

  const setCuotasUserMax = (uid, val) => {
    const id = String(uid);
    setCuotasConfig(prev => prev.map(c => c.user_id === id ? { ...c, max_cuotas: Number(val) } : c));
  };

  const saveCuotasConfig = async () => {
    if (!cuotasModal) return;
    setSavingCuotas(true);
    try {
      const res = await fetch(`${API}/admin/plan-cuentas/${cuotasModal.id}/cuotas-config`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ usuarios_cuotas: cuotasConfig }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "No se pudo guardar");
      }
      toast.success("Configuración de cuotas guardada");
      setCuotasModal(null);
      fetchCuentas();
    } catch (e) {
      toast.error(e.message || "Error al guardar");
    } finally {
      setSavingCuotas(false);
    }
  };

  const setUso = (uso) => {
    const cfg = USOS.find(u => u.value === uso);
    setForm(f => ({ ...f, uso, tipo: cfg?.tipo || f.tipo }));
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.nombre.trim()) { toast.error("Ingresá el nombre de la cuenta"); return; }
    try {
      const url    = editing ? `${API}/admin/plan-cuentas/${editing.id}` : `${API}/admin/plan-cuentas`;
      const method = editing ? "PUT" : "POST";
      const res    = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          ...form,
          logo_tipo:        logo,
          dias_vencimiento: Number(form.dias_vencimiento || 0),
          fecha_vencimiento_fija: (form.uso === "venta_credito" || form.uso === "compra_credito") && form.fecha_vencimiento_fija ? form.fecha_vencimiento_fija : null,
          max_cuotas:       Number(form.max_cuotas || 6),
          tasa_interes:     Number(form.tasa_interes || 0),
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "No se pudo guardar la cuenta");
      }
      toast.success(editing ? "Cuenta actualizada" : "Cuenta creada");
      setShowForm(false);
      fetchCuentas();
    } catch (e) {
      toast.error(e.message || "No se pudo guardar");
    }
  };

  const remove = async (cuenta) => {
    if (cuenta.sistema) { toast.error("Las cuentas base del sistema no se pueden eliminar."); return; }
    if (!window.confirm(`¿Eliminar "${cuenta.nombre}"? Si tiene documentos asociados no se podrá.`)) return;
    try {
      const res = await fetch(`${API}/admin/plan-cuentas/${cuenta.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "No se pudo eliminar");
      }
      toast.success("Cuenta eliminada");
      fetchCuentas();
    } catch (e) {
      toast.error(e.message || "No se pudo eliminar");
    }
  };

  // ── Acceso de usuarios ────────────────────────────────────────────────────────

  const openAcceso = (cuenta) => {
    setAccesoModal(cuenta);
    setAccesoIds((cuenta.usuarios_acceso_ids || []).map(String));
  };

  const toggleAccesoUser = (uid) => {
    const id = String(uid);
    setAccesoIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const saveAcceso = async () => {
    if (!accesoModal) return;
    setSavingAcceso(true);
    try {
      const res = await fetch(`${API}/admin/plan-cuentas/${accesoModal.id}/acceso`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ usuarios_acceso_ids: accesoIds }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "No se pudo guardar el acceso");
      }
      toast.success("Acceso actualizado");
      setAccesoModal(null);
      fetchCuentas();
    } catch (e) {
      toast.error(e.message || "No se pudo guardar");
    } finally {
      setSavingAcceso(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-arandu-dark text-white p-6">
      <div className="max-w-6xl mx-auto space-y-6">

        {/* Header */}
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Link to="/sistema" className="p-2 rounded-lg hover:bg-white/10 text-slate-400 hover:text-white">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <BookOpen className="w-6 h-6 text-cyan-400" /> Plan de cuentas
              </h1>
              <p className="text-slate-400 text-sm">
                Cuentas por cobrar y pagar de {activeEmpresaPropia?.nombre || "la empresa activa"}
              </p>
            </div>
          </div>
          {canCreate && (
            <button
              onClick={openNew}
              className="px-4 py-2 rounded-lg bg-cyan-600 hover:bg-cyan-700 text-sm font-medium flex items-center gap-2"
            >
              <Plus className="w-4 h-4" /> Nueva cuenta
            </button>
          )}
        </div>

        {/* Grilla por uso */}
        <div className="grid lg:grid-cols-2 gap-4">
          {grouped.map(group => (
            <div key={group.value} className="rounded-xl border border-white/10 bg-arandu-dark-light overflow-hidden">
              <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
                <div>
                  <h2 className="font-semibold flex items-center gap-2">
                    {group.label}
                    {group.soloInfo && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/25">
                        Solo registro — no usable en facturas
                      </span>
                    )}
                  </h2>
                  <p className="text-xs text-slate-500">
                    {group.soloInfo
                      ? group.desc
                      : group.tipo === "cobrar" ? "Cuenta por cobrar" : "Cuenta por pagar"}
                  </p>
                </div>
                <span className="text-xs text-slate-500">{group.cuentas.length} cuenta(s)</span>
              </div>

              <div className="divide-y divide-white/5">
                {loading ? (
                  <div className="p-4 text-slate-400 text-sm">Cargando...</div>
                ) : group.cuentas.length === 0 ? (
                  <div className="p-4 text-slate-500 text-sm">Sin cuentas para este uso.</div>
                ) : group.cuentas.map(c => (
                  <div key={c.id} className="p-4 flex items-center justify-between gap-3 hover:bg-white/[0.03]">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium truncate">{c.nombre}</p>

                        {c.sistema && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-700/60 text-slate-300 border border-slate-600/40 flex items-center gap-1">
                            <Lock className="w-2.5 h-2.5" /> Sistema
                          </span>
                        )}
                        {c.predeterminada && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/25 flex items-center gap-1">
                            <CheckCircle className="w-3 h-3" /> Predeterminada
                          </span>
                        )}
                        {c.activa === false && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-500/15 text-slate-300 border border-slate-500/25">
                            Inactiva
                          </span>
                        )}
                        {!c.sistema && !c.es_cuotas && (c.usuarios_acceso_ids || []).length > 0 && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/25 flex items-center gap-1">
                            <Shield className="w-2.5 h-2.5" /> {c.usuarios_acceso_ids.length} usuario(s)
                          </span>
                        )}
                        {c.es_cuotas && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-violet-500/15 text-violet-300 border border-violet-500/25 flex items-center gap-1">
                            <CreditCard className="w-2.5 h-2.5" />
                            Cuotas · max {c.max_cuotas ?? 6}
                            {Number(c.tasa_interes) > 0 && ` · ${c.tasa_interes}%`}
                          </span>
                        )}
                        {c.es_cuotas && (c.usuarios_cuotas || []).length > 0 && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/25 flex items-center gap-1">
                            <Users className="w-2.5 h-2.5" /> {c.usuarios_cuotas.length} usuario(s) habilitado(s)
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-slate-500">
                        {group.soloInfo
                          ? "Gestionado automáticamente por el módulo correspondiente"
                          : c.es_cuotas
                          ? `Hasta ${c.max_cuotas ?? 6} cuotas${Number(c.tasa_interes) > 0 ? ` · ${c.tasa_interes}% interés` : " · sin interés"}`
                          : c.fecha_vencimiento_fija
                          ? `Vence el ${String(c.fecha_vencimiento_fija).split("-").reverse().join("/")} (fecha fija)`
                          : `Vencimiento: ${Number(c.dias_vencimiento || 0)} día(s)`}
                      </p>
                    </div>

                    <div className="flex items-center gap-1">
                      {/* Cuotas accounts: show cuotas-config button; regular: show acceso button */}
                      {c.es_cuotas ? (
                        canGestionarCuotas && !group.soloInfo && (
                          <button
                            onClick={() => openCuotasModal(c)}
                            className="p-2 rounded-lg text-slate-400 hover:text-violet-300 hover:bg-violet-500/10"
                            title="Gestionar usuarios y tope de cuotas"
                          >
                            <CreditCard className="w-4 h-4" />
                          </button>
                        )
                      ) : (
                        canAsignarAcceso && !c.sistema && !group.soloInfo && (
                          <button
                            onClick={() => openAcceso(c)}
                            className="p-2 rounded-lg text-slate-400 hover:text-amber-300 hover:bg-amber-500/10"
                            title="Gestionar acceso de usuarios"
                          >
                            <Users className="w-4 h-4" />
                          </button>
                        )
                      )}
                      {canEdit && !group.soloInfo && (
                        <button
                          onClick={() => openEdit(c)}
                          className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-white/10"
                          title="Editar"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                      )}
                      {canDelete && !c.sistema && (
                        <button
                          onClick={() => remove(c)}
                          className="p-2 rounded-lg text-slate-400 hover:text-red-300 hover:bg-red-500/10"
                          title="Eliminar"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Modal crear / editar ──────────────────────────────────────────────── */}
      {showForm && (
        <div
          className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
        >
          <form
            onSubmit={save}
            className="w-full max-w-lg rounded-2xl bg-arandu-dark-light border border-white/10 shadow-2xl overflow-hidden"
          >
            <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
              <h2 className="font-semibold flex items-center gap-2">
                {editing?.sistema && <Lock className="w-4 h-4 text-slate-400" />}
                {editing ? "Editar cuenta" : "Nueva cuenta"}
              </h2>
              <button type="button" onClick={() => setShowForm(false)} className="p-2 rounded-lg hover:bg-white/10 text-slate-400">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              <div>
                <label className="text-slate-400 text-sm mb-1 block">Nombre *</label>
                <input
                  value={form.nombre}
                  onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))}
                  className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
                  placeholder="Venta 30 días"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-slate-400 text-sm mb-1 block">Uso</label>
                  <select
                    value={form.uso}
                    onChange={e => setUso(e.target.value)}
                    disabled={!!editing?.sistema}
                    className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {USOS.filter(u => !u.soloInfo).map(u => <option key={u.value} value={u.value}>{u.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-slate-400 text-sm mb-1 block">Días vencimiento</label>
                  <input
                    type="number"
                    min="0"
                    max={editing ? editing.dias_vencimiento : undefined}
                    disabled={!!form.fecha_vencimiento_fija}
                    value={form.dias_vencimiento}
                    onChange={e => setForm(f => ({ ...f, dias_vencimiento: e.target.value }))}
                    className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
                  />
                </div>
              </div>

              {(form.uso === "venta_credito" || form.uso === "compra_credito") && (
                <div>
                  <label className="text-slate-400 text-sm mb-1 block">Fecha de vencimiento fija (opcional)</label>
                  <div className="flex gap-2">
                    <DateInput
                      value={form.fecha_vencimiento_fija || ""}
                      onChange={e => setForm(f => ({ ...f, fecha_vencimiento_fija: e.target.value }))}
                      className="flex-1 bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
                    />
                    {form.fecha_vencimiento_fija && (
                      <button type="button" onClick={() => setForm(f => ({ ...f, fecha_vencimiento_fija: "" }))}
                        className="px-3 py-2 bg-white/5 hover:bg-white/10 text-slate-300 rounded-lg text-xs">Quitar</button>
                    )}
                  </div>
                  <p className="text-slate-500 text-xs mt-1">Si la definís, toda operación con esta cuenta vence ese día calendario (ej. 30/03/2027), sin importar la fecha de emisión. Los "Días vencimiento" se ignoran.</p>
                </div>
              )}

              {/* Cuotas fields — only for venta_credito / compra_credito */}
              {(form.uso === "venta_credito" || form.uso === "compra_credito") && (
                <div className="rounded-lg border border-violet-500/20 bg-violet-500/5 p-4 space-y-3">
                  <p className="text-xs font-medium text-violet-300 flex items-center gap-1.5">
                    <CreditCard className="w-3.5 h-3.5" /> Configuración de cuotas
                  </p>

                  <label className="flex items-center gap-3 text-sm text-slate-300">
                    <input
                      type="checkbox"
                      checked={form.es_cuotas}
                      onChange={e => setForm(f => ({ ...f, es_cuotas: e.target.checked }))}
                    />
                    Esta cuenta permite pago en cuotas
                  </label>

                  {form.es_cuotas && (
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-slate-400 text-xs mb-1 block">Máx. cuotas (global)</label>
                        <input
                          type="number"
                          min="1"
                          max="60"
                          value={form.max_cuotas}
                          onChange={e => setForm(f => ({ ...f, max_cuotas: e.target.value }))}
                          className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
                        />
                      </div>
                      <div>
                        <label className="text-slate-400 text-xs mb-1 block flex items-center gap-1">
                          <Percent className="w-3 h-3" /> Tasa interés (%)
                        </label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={form.tasa_interes}
                          onChange={e => setForm(f => ({ ...f, tasa_interes: e.target.value }))}
                          className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
                          placeholder="0"
                        />
                      </div>
                    </div>
                  )}
                  {form.es_cuotas && (
                    <p className="text-[11px] text-slate-500">
                      El tope por usuario se configura desde el botón de cuotas en la cuenta. Sin usuarios habilitados = nadie puede usar cuotas.
                    </p>
                  )}
                </div>
              )}

              <label className="flex items-center gap-3 text-sm text-slate-300">
                <input
                  type="checkbox"
                  checked={form.activa}
                  onChange={e => setForm(f => ({ ...f, activa: e.target.checked }))}
                />
                Cuenta activa
              </label>

              <div>
                <label className="text-slate-400 text-sm mb-1 block">Notas</label>
                <textarea
                  value={form.notas || ""}
                  onChange={e => setForm(f => ({ ...f, notas: e.target.value }))}
                  className="w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm min-h-[80px]"
                />
              </div>
            </div>

            <div className="px-5 py-4 border-t border-white/10 flex justify-end gap-3">
              <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-sm text-slate-400 hover:text-white">
                Cancelar
              </button>
              <button type="submit" className="px-4 py-2 rounded-lg bg-cyan-600 hover:bg-cyan-700 text-sm font-medium flex items-center gap-2">
                <Save className="w-4 h-4" /> Guardar
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ── Modal cuotas por usuario ─────────────────────────────────────────── */}
      {cuotasModal && (
        <div
          className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
        >
          <div className="w-full max-w-md rounded-2xl bg-arandu-dark-light border border-white/10 shadow-2xl overflow-hidden">
            <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
              <div>
                <h2 className="font-semibold flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-violet-400" /> Usuarios habilitados — "{cuotasModal.nombre}"
                </h2>
                <p className="text-xs text-slate-400 mt-0.5">
                  Solo usuarios marcados aquí pueden seleccionar esta cuenta en cuotas. Sin selección = nadie habilitado.
                </p>
              </div>
              <button onClick={() => setCuotasModal(null)} className="p-2 rounded-lg hover:bg-white/10 text-slate-400">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-5 space-y-2 max-h-72 overflow-y-auto">
              {usuarios.length === 0 ? (
                <p className="text-slate-500 text-sm text-center py-6">
                  No hay usuarios restringidos asignados a esta empresa.
                </p>
              ) : usuarios.map(u => {
                const entry = cuotasConfig.find(c => c.user_id === String(u.id));
                const checked = !!entry;
                return (
                  <div key={u.id} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-white/5">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleCuotasUser(u.id)}
                      className="w-4 h-4 flex-shrink-0"
                    />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">{u.name}</p>
                      <p className="text-xs text-slate-500 truncate">{u.email}</p>
                    </div>
                    {checked && (
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <label className="text-xs text-slate-400">Máx.</label>
                        <input
                          type="number"
                          min="1"
                          max={cuotasModal.max_cuotas ?? 60}
                          value={entry.max_cuotas}
                          onChange={e => setCuotasUserMax(u.id, e.target.value)}
                          className="w-16 bg-arandu-dark border border-white/10 rounded px-2 py-1 text-white text-xs text-center"
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="px-5 py-4 border-t border-white/10 flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={() => setCuotasConfig([])}
                className="text-xs text-slate-500 hover:text-slate-300 underline"
              >
                Quitar todos (nadie habilitado)
              </button>
              <div className="flex gap-3">
                <button onClick={() => setCuotasModal(null)} className="px-4 py-2 text-sm text-slate-400 hover:text-white">
                  Cancelar
                </button>
                <button
                  onClick={saveCuotasConfig}
                  disabled={savingCuotas}
                  className="px-4 py-2 rounded-lg bg-violet-600 hover:bg-violet-700 text-sm font-medium flex items-center gap-2 disabled:opacity-60"
                >
                  <Save className="w-4 h-4" />
                  {savingCuotas ? "Guardando..." : "Guardar"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal asignar acceso ──────────────────────────────────────────────── */}
      {accesoModal && (
        <div
          className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
        >
          <div className="w-full max-w-md rounded-2xl bg-arandu-dark-light border border-white/10 shadow-2xl overflow-hidden">
            <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
              <div>
                <h2 className="font-semibold flex items-center gap-2">
                  <Users className="w-4 h-4 text-amber-400" /> Acceso a "{accesoModal.nombre}"
                </h2>
                <p className="text-xs text-slate-400 mt-0.5">
                  Sin selección = todos los usuarios pueden ver esta cuenta
                </p>
              </div>
              <button onClick={() => setAccesoModal(null)} className="p-2 rounded-lg hover:bg-white/10 text-slate-400">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-5 space-y-2 max-h-72 overflow-y-auto">
              {usuarios.length === 0 ? (
                <p className="text-slate-500 text-sm text-center py-6">
                  No hay usuarios restringidos asignados a esta empresa.
                </p>
              ) : usuarios.map(u => (
                <label key={u.id} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-white/5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={accesoIds.includes(String(u.id))}
                    onChange={() => toggleAccesoUser(u.id)}
                    className="w-4 h-4 flex-shrink-0"
                  />
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{u.name}</p>
                    <p className="text-xs text-slate-500 truncate">{u.email}</p>
                  </div>
                </label>
              ))}
            </div>

            <div className="px-5 py-4 border-t border-white/10 flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={() => setAccesoIds([])}
                className="text-xs text-slate-500 hover:text-slate-300 underline"
              >
                Limpiar (acceso libre)
              </button>
              <div className="flex gap-3">
                <button onClick={() => setAccesoModal(null)} className="px-4 py-2 text-sm text-slate-400 hover:text-white">
                  Cancelar
                </button>
                <button
                  onClick={saveAcceso}
                  disabled={savingAcceso}
                  className="px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-sm font-medium flex items-center gap-2 disabled:opacity-60"
                >
                  <Save className="w-4 h-4" />
                  {savingAcceso ? "Guardando..." : "Guardar acceso"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
